/**
 * Rows for lists of referring blocks (references, instances, a person's tasks and meetings):
 * grouped by their day (newest first) or page (last changed first), with a path label when the
 * block is nested; the block and its visible children are editable where they are (D66).
 */
import { plainText, visibleRows, type Graph, type Node, type NodeId } from '@solar/core'
import type { ClientStore, ViewRow } from '../client/store.ts'
import { dayTitle } from './dates.ts'

export function label(n: Node): string {
  if (n.kind === 'journal' && n.date) return dayTitle(n.date).title
  const s = n.title ?? plainText(n.content)
  return s.length > 40 ? s.slice(0, 39) + '…' : s || '(empty)'
}

export function hrefOf(n: Node): string {
  return n.kind === 'journal' && n.date ? `/d/${n.date}` : `/n/${n.id}`
}

export function ancestors(g: Graph, id: NodeId): Node[] {
  const out: Node[] = []
  for (let cur = g.get(id)?.parentId ?? null; cur !== null; cur = g.get(cur)?.parentId ?? null) {
    const n = g.get(cur)
    if (!n) break
    out.unshift(n)
  }
  return out
}

export function within(g: Graph, id: NodeId, root: NodeId): boolean {
  for (let cur: NodeId | null = id; cur !== null; cur = g.get(cur)?.parentId ?? null) if (cur === root) return true
  return false
}

/**
 * Reference rows: grouped by their day or page (days newest first, then pages by last change), a
 * path label when the block is nested, the block itself and its visible children. Blocks inside
 * the node, and blocks whose ancestor is already listed, are left out.
 */
export function referenceRows(store: ClientStore, node: Node, ids: readonly NodeId[], prefix: string): { rows: ViewRow[]; count: number } {
  const g = store.g
  const listed = ids.filter((id) => g.isLive(id) && !within(g, id, node.id))
  const set = new Set(listed)
  const top = listed.filter((id) => !ancestors(g, id).some((a) => set.has(a.id)))
  const groups = new Map<NodeId, NodeId[]>()
  for (const id of top) {
    const root = ancestors(g, id)[0]?.id ?? id
    let l = groups.get(root)
    if (!l) groups.set(root, (l = []))
    l.push(id)
  }
  const order = [...groups.keys()].sort((a, b) => {
    const x = g.must(a)
    const y = g.must(b)
    if (x.kind === 'journal' && y.kind === 'journal') return (y.date ?? '').localeCompare(x.date ?? '')
    if (x.kind === 'journal') return -1
    if (y.kind === 'journal') return 1
    return y.updatedAt - x.updatedAt
  })
  const rows: ViewRow[] = []
  for (const root of order) {
    const r = g.must(root)
    rows.push({ type: 'label', key: `${prefix}g:${root}`, level: 'group', text: label(r), href: hrefOf(r) })
    const members = new Set(groups.get(root))
    // Document order inside the group.
    const inOrder: NodeId[] = []
    const walk = (id: NodeId): void => {
      for (const k of g.children(id)) {
        if (!g.isLive(k)) continue
        if (members.has(k)) inOrder.push(k)
        else walk(k)
      }
    }
    walk(root)
    for (const id of inOrder) {
      const parent = g.must(id).parentId ?? root
      const path = ancestors(g, id).slice(1)
      if (path.length > 0) rows.push({ type: 'label', key: `${prefix}p:${id}`, level: 'path', text: path.map(label).join(' › ') })
      rows.push({ type: 'block', key: `${prefix}${id}`, id, depth: 0, root: parent })
      for (const v of visibleRows(g, id)) rows.push({ type: 'block', key: `${prefix}${v.id}`, id: v.id, depth: v.depth + 1, root: parent })
    }
  }
  return { rows, count: top.length }
}

